import { useMemo } from 'react'
import { usePresentationStore } from '@/stores/presentation.ts'
import {
  anonymizeAmount,
  anonymizeCode,
  anonymizeDocNumber,
  anonymizeEmail,
  anonymizeFreeText,
  anonymizeIban,
  anonymizeInteger,
  anonymizeIp,
  anonymizeMac,
  anonymizeName,
  anonymizePhone,
  anonymizeToken,
} from '@/lib/anonymize.ts'

/**
 * Liefert Transform-Funktionen, die bei aktivem Praesentations-Modus sensible
 * Werte durch deterministische Platzhalter ersetzen – andernfalls geben sie
 * den Originalwert durch. So koennen Aufrufstellen einfach `name(x)` schreiben,
 * ohne selber `if (enabled)` pruefen zu muessen.
 *
 * Zusaetzlich werden `fmtCurrency` und `fmtInteger` geliefert, die Betraege
 * direkt anonymisieren UND formatieren.
 */
export function useAnonymize() {
  const enabled = usePresentationStore((s) => s.anonymize)

  return useMemo(() => {
    const pass = <T,>(v: T) => v

    const amount = (v: number | null | undefined): number => {
      if (v == null || !Number.isFinite(v)) return v ?? 0
      return enabled ? anonymizeAmount(v) : v
    }

    const integer = (v: number | null | undefined): number => {
      if (v == null || !Number.isFinite(v)) return v ?? 0
      return enabled ? anonymizeInteger(v) : v
    }

    const name = (v: string | null | undefined): string => {
      if (v == null) return ''
      return enabled ? anonymizeName(v) : v
    }

    const code = (v: string | null | undefined): string => {
      if (v == null) return ''
      return enabled ? anonymizeCode(v) : v
    }

    const docNum = (v: string | number | null | undefined): string => {
      if (v == null) return ''
      return enabled ? anonymizeDocNumber(v) : String(v)
    }

    const iban = (v: string | null | undefined): string => {
      if (!v) return ''
      return enabled ? anonymizeIban(v) : v
    }

    const email = (v: string | null | undefined): string => {
      if (!v) return ''
      return enabled ? anonymizeEmail(v) : v
    }

    const phone = (v: string | null | undefined): string => {
      if (!v) return ''
      return enabled ? anonymizePhone(v) : v
    }

    const freeText = (v: string | null | undefined): string => {
      if (!v) return ''
      return enabled ? anonymizeFreeText(v) : v
    }

    const token = (v: string | null | undefined): string => {
      if (v == null) return ''
      return enabled ? anonymizeToken(String(v)) : String(v)
    }

    const ip = (v: string | null | undefined): string => {
      if (!v) return ''
      return enabled ? anonymizeIp(v) : v
    }

    const mac = (v: string | null | undefined): string => {
      if (!v) return ''
      return enabled ? anonymizeMac(v) : v
    }

    const fmtCurrency = (v: number | null | undefined, currency = 'EUR'): string => {
      const x = amount(v)
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(x)
    }

    /**
     * Wie `formatMoney` in der Buchhaltung: `1.234,56 EUR` (mit Waehrungs-Code
     * als Suffix) statt `1.234,56 €`. Gibt „–" zurueck wenn null/undefined.
     */
    const fmtMoney = (v: number | null | undefined, currency = 'EUR'): string => {
      if (v == null) return '–'
      const x = amount(v)
      return x.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + currency
    }

    const fmtInteger = (v: number | null | undefined): string => {
      const x = integer(v)
      return new Intl.NumberFormat('de-DE').format(x)
    }

    return {
      enabled,
      pass,
      amount,
      integer,
      name,
      code,
      docNum,
      iban,
      email,
      phone,
      freeText,
      token,
      ip,
      mac,
      fmtCurrency,
      fmtMoney,
      fmtInteger,
    }
  }, [enabled])
}

export type Anonymizer = ReturnType<typeof useAnonymize>
